import React from 'react';
import { Card, Row, Col, Button, Space, Typography, message } from 'antd';
import { PageLoading } from '@/shared/components/ui';
import { useParams, useNavigate } from 'react-router-dom';
import { EditOutlined, PrinterOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useOrderDetail } from '@/shared/api/orderApi';
import { useAuthStore } from '@/stores/authStore';
import OrderInfoCard from './components/OrderInfoCard';
import OrderItemsTable from './components/OrderItemsTable';
import StatusHistoryTimeline from './components/StatusHistoryTimeline';

const { Title, Text } = Typography;

export interface OrderDetailPageProps {
  /** 公司ID */
  companyId?: number;
  /** 是否只读模式 */
  readOnly?: boolean;
  /** 是否可以编辑 */
  canEdit?: boolean;
  /** 是否可以打印 */
  canPrint?: boolean;
  /** 是否可以分享 */
  canShare?: boolean;
}

const OrderDetailPage: React.FC<OrderDetailPageProps> = ({
  companyId: propCompanyId,
  readOnly = false,
  canEdit = true,
  canPrint = true,
  canShare = true
}) => {
  const { t } = useTranslation(['modules', 'common']);
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user, isRestored } = useAuthStore();

  if (!isRestored) {
    return <PageLoading />;
  }

  // 检查用户登录状态（在AuthRequiredRoute内部，用户应该已登录）
  if (!user && !propCompanyId) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Title level={4} type="danger">
            {t('modules:order.detail.loadError', '加载订单详情失败')}
          </Title>
          <Text type="secondary">
            {t('modules:order.noCompanyErrorDesc', '请重新登录或联系管理员')}
          </Text>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => navigate('/login')}>
              {t('common:login', '登录')}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // 计算实际使用的companyId：
  // 1. 优先使用props传入的值（允许管理员查看特定公司）
  // 2. 其次使用用户的实际company_id（默认行为，符合权限隔离）
  // 3. 最后使用默认值1（兼容性回退）
  const companyId = propCompanyId ?? user?.company_id ?? 1;

  // 获取订单详情
  const { data, isLoading, error, refetch } = useOrderDetail(orderId || '', companyId);

  // 处理返回
  const handleBack = () => {
    navigate('/orders');
  };

  // 处理编辑
  const handleEdit = () => {
    // 编辑功能开发中
    message.info(t('modules:order.detail.editMessage', '编辑功能开发中'));
    // 暂时不跳转
    // navigate(`/orders/${orderId}/edit`);
  };

  // 处理打印
  const handlePrint = () => {
    window.print();
  };

  // 处理分享
  const handleShare = () => {
    // 实现分享逻辑
    message.info(t('modules:order.detail.shareMessage', '分享功能开发中'));
  };

  // 处理刷新
  const handleRefresh = () => {
    refetch();
    message.success(t('modules:order.detail.refreshSuccess', '数据已刷新'));
  };

  // 显示错误
  if (error) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Title level={4} type="danger">
            {t('modules:order.detail.loadError', '加载订单详情失败')}
          </Title>
          <Text type="secondary">
            {error instanceof Error ? error.message : String(error)}
          </Text>
          <div style={{ marginTop: 16 }}>
            <Space>
              <Button onClick={handleBack}>
                {t('common:back', '返回')}
              </Button>
              <Button type="primary" onClick={handleRefresh}>
                {t('common:retry', '重试')}
              </Button>
            </Space>
          </div>
        </div>
      </Card>
    );
  }

  if (isLoading || !data) {
    return <PageLoading />;
  }

  // 检查订单数据
  const orderData = data.data;
  if (!orderData) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Title level={4}>
            {t('modules:order.detail.notFound', '订单不存在')}
          </Title>
          <Text type="secondary">
            {t('modules:order.detail.notFoundDesc', '请求的订单不存在或已被删除')}
          </Text>
          <div style={{ marginTop: 16 }}>
            <Button onClick={handleBack}>
              {t('common:back', '返回')}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="order-detail-page">
      {/* 头部操作栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button onClick={handleBack}>
                {t('common:back', '返回')}
              </Button>
              <Title level={4} style={{ margin: 0 }}>
                {t('modules:order.detail.title', '订单详情')} - {orderData.order_id}
              </Title>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<EditOutlined />}
                onClick={handleEdit}
                disabled={readOnly || !canEdit}
              >
                {t('modules:order.detail.edit', '编辑')}
              </Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={handlePrint}
                disabled={!canPrint}
              >
                {t('modules:order.detail.print', '打印')}
              </Button>
              <Button
                icon={<ShareAltOutlined />}
                onClick={handleShare}
                disabled={!canShare}
              >
                {t('modules:order.detail.share', '分享')}
              </Button>
              <Button type="primary" onClick={handleRefresh}>
                {t('modules:order.detail.refresh', '刷新')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 订单信息 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <OrderInfoCard orderData={orderData} />
        </Col>
        <Col xs={24} lg={8}>
          <StatusHistoryTimeline
            statusHistory={orderData.status_history || []}
            currentStatus={orderData.status}
          />
        </Col>
      </Row>

      {/* 订单商品 */}
      <Card style={{ marginTop: 16 }}>
        <Title level={5} style={{ marginBottom: 16 }}>
          {t('modules:order.detail.itemsTitle', '订单商品')}
        </Title>
        <OrderItemsTable items={orderData.items || []} />
      </Card>




    </div>
  );
};

export default OrderDetailPage;